import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Upload, Download, FileText, FileSpreadsheet } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface RestaurantCSVImportExportProps {
  onImportComplete?: () => void;
}

export function RestaurantCSVImportExport({ onImportComplete }: RestaurantCSVImportExportProps) {
  const [isImporting, setIsImporting] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<any>(null);
  const { toast } = useToast();

  const handleExportCSV = async () => {
    try {
      window.location.href = '/api/admin/restaurant/products/export/csv';
    } catch (error) {
      toast({
        title: "Export Failed",
        description: "Failed to export products",
        variant: "destructive",
      });
    }
  };

  const handleExportXLSX = async () => {
    try {
      window.location.href = '/api/admin/restaurant/products/export/xlsx';
    } catch (error) {
      toast({
        title: "Export Failed",
        description: "Failed to export products",
        variant: "destructive",
      });
    }
  };

  const handleDownloadTemplate = () => {
    window.location.href = '/api/admin/restaurant/products/template/csv';
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImportFile(file);
      setShowImportDialog(true);
    }
  };

  const handleImport = async () => {
    if (!importFile) return;

    setIsImporting(true);
    try {
      const content = await importFile.text();
      const result = await apiRequest('POST', '/api/admin/restaurant/products/import/csv', {
        csvContent: content
      });

      setImportResult(result);
      
      if (result.success > 0) {
        toast({
          title: "Import Successful",
          description: `${result.success} products imported successfully`,
        });
        
        if (onImportComplete) {
          onImportComplete();
        }
      }
      
      if (result.failed > 0) {
        toast({
          title: "Import Completed with Errors",
          description: `${result.failed} products failed to import`,
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Import Failed",
        description: error instanceof Error ? error.message : "Failed to import products",
        variant: "destructive",
      });
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <>
      <div className="flex flex-wrap gap-4">
        {/* Export Section */}
        <div className="flex-1 min-w-[200px]">
          <Label className="text-sm font-medium mb-2 block">Export Products</Label>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportCSV}
              className="flex-1"
            >
              <FileText className="h-4 w-4 mr-2" />
              CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportXLSX}
              className="flex-1"
            >
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              Excel
            </Button>
          </div>
        </div>

        {/* Import Section */}
        <div className="flex-1 min-w-[200px]">
          <Label className="text-sm font-medium mb-2 block">Import Products</Label>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadTemplate}
              className="flex-1"
            >
              <Download className="h-4 w-4 mr-2" />
              Template
            </Button>
            <div className="flex-1">
              <Input
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="hidden"
                id="csv-upload"
              />
              <Label htmlFor="csv-upload">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => document.getElementById('csv-upload')?.click()}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Import
                </Button>
              </Label>
            </div>
          </div>
        </div>
      </div>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Restaurant Products</DialogTitle>
            <DialogDescription>
              Import products from CSV file: {importFile?.name}
            </DialogDescription>
          </DialogHeader>

          {importResult && (
            <div className="space-y-2">
              <p className="text-sm">
                <span className="font-medium">Success:</span> {importResult.success} products
              </p>
              <p className="text-sm">
                <span className="font-medium">Failed:</span> {importResult.failed} products
              </p>
              
              {importResult.errors && importResult.errors.length > 0 && (
                <div className="mt-2">
                  <p className="text-sm font-medium mb-1">Errors:</p>
                  <div className="max-h-40 overflow-y-auto bg-gray-50 p-2 rounded text-xs">
                    {importResult.errors.map((error: any, index: number) => (
                      <div key={index} className="text-red-600">
                        Row {error.row}: {error.error}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {!importResult && (
            <div className="py-4">
              <p className="text-sm text-gray-600 mb-4">
                This will import products from the selected CSV file. Existing products with the same name will be updated.
              </p>
              <p className="text-sm text-gray-600">
                Make sure your CSV file has the correct format with columns: Name, Category, Description, Price Per Unit, Unit, Active, Display Order
              </p>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowImportDialog(false);
                setImportResult(null);
                setImportFile(null);
              }}
            >
              {importResult ? 'Close' : 'Cancel'}
            </Button>
            {!importResult && (
              <Button
                onClick={handleImport}
                disabled={isImporting}
              >
                {isImporting ? 'Importing...' : 'Import'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}